import { TOUCH } from "../config/controls";
import { NO_STICK, type StickSample } from "../systems/inputMapping";

export interface TouchSample {
    stick: StickSample;
    jump: boolean;
    pause: boolean;
}

const STYLE = `
#touch-controls { position: fixed; inset: 0; pointer-events: none; touch-action: none; user-select: none;
    -webkit-user-select: none; -webkit-touch-callout: none; font-family: sans-serif; z-index: 10; }
#touch-controls > * { position: absolute; pointer-events: auto; touch-action: none; border-radius: 50%;
    border: 3px solid rgba(0,0,0,0.6); background: rgba(43,43,43,0.45); color: #fff8e7; font-weight: bold; }
#touch-controls .down { background: rgba(255,248,231,0.55); color: #2b2b2b; }
#touch-controls .tc-stick { left: calc(24px + env(safe-area-inset-left)); bottom: calc(24px + env(safe-area-inset-bottom));
    width: ${TOUCH.stickRadiusPx * 2}px; height: ${TOUCH.stickRadiusPx * 2}px; }
#touch-controls .tc-knob { position: absolute; left: 50%; top: 50%; width: 60px; height: 60px; margin: -30px 0 0 -30px;
    border-radius: 50%; border: 3px solid rgba(0,0,0,0.6); background: rgba(255,248,231,0.7); pointer-events: none; }
#touch-controls .tc-jump { right: calc(24px + env(safe-area-inset-right)); bottom: calc(24px + env(safe-area-inset-bottom));
    width: 110px; height: 110px; font-size: 18px; }
#touch-controls .tc-pause { right: calc(16px + env(safe-area-inset-right)); top: calc(16px + env(safe-area-inset-top));
    width: 52px; height: 52px; font-size: 20px; }
`;

let root: HTMLElement | null = null;
let knob: HTMLElement;
let stickFinger: number | null = null;
let stick: StickSample = NO_STICK;
let jumped = false;
let paused = false;

function moveStick(base: HTMLElement, e: PointerEvent) {
    const box = base.getBoundingClientRect();
    const r = TOUCH.stickRadiusPx;
    let x = (e.clientX - (box.left + box.width / 2)) / r;
    let y = (e.clientY - (box.top + box.height / 2)) / r;
    const len = Math.hypot(x, y);
    if (len > 1) [x, y] = [x / len, y / len];
    stick = { x, y };
    knob.style.transform = `translate(${x * r}px, ${y * r}px)`;
}

function releaseStick() {
    stickFinger = null;
    stick = NO_STICK;
    knob.style.transform = "";
}

function addStick(parent: HTMLElement) {
    const base = document.createElement("div");
    base.className = "tc-stick";
    knob = document.createElement("div");
    knob.className = "tc-knob";
    base.append(knob);

    base.addEventListener("pointerdown", (e) => {
        if (stickFinger !== null) return;
        stickFinger = e.pointerId;
        base.setPointerCapture(e.pointerId);
        moveStick(base, e);
    });
    base.addEventListener("pointermove", (e) => {
        if (e.pointerId === stickFinger) moveStick(base, e);
    });
    const end = (e: PointerEvent) => {
        if (e.pointerId === stickFinger) releaseStick();
    };
    base.addEventListener("pointerup", end);
    base.addEventListener("pointercancel", end);
    parent.append(base);
}

function addTapButton(parent: HTMLElement, cls: string, label: string, onTap: () => void) {
    const button = document.createElement("button");
    button.className = cls;
    button.textContent = label;
    button.addEventListener("pointerdown", () => {
        button.classList.add("down");
        onTap();
    });
    const up = () => button.classList.remove("down");
    button.addEventListener("pointerup", up);
    button.addEventListener("pointercancel", up);
    button.addEventListener("pointerleave", up);
    parent.append(button);
}

function build(): HTMLElement {
    const style = document.createElement("style");
    style.textContent = STYLE;
    document.head.append(style);

    const el = document.createElement("div");
    el.id = "touch-controls";
    addStick(el);
    addTapButton(el, "tc-jump", "JUMP", () => (jumped = true));
    addTapButton(el, "tc-pause", "✕", () => (paused = true));
    el.addEventListener("pointerdown", (e) => e.preventDefault());
    el.addEventListener("contextmenu", (e) => e.preventDefault());
    document.body.append(el);
    return el;
}

export function showTouchControls(visible: boolean) {
    if (!visible && !root) return;
    root ??= build();
    root.style.display = visible ? "" : "none";
    releaseStick();
    jumped = false;
    paused = false;
}

/** The stick is read live every frame; taps fire once per touch and are consumed here. */
export function readTouch(): TouchSample {
    const sample = { stick, jump: jumped, pause: paused };
    jumped = false;
    paused = false;
    return sample;
}
